import { Close, MoreVertIcon } from '@tunasong/icons'
import {
  Button,
  IconButton,
  Menu,
  MenuCheckboxItem,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '@tunasong/ui-lib'
import type { FC, MouseEvent, ReactNode } from 'react'
import { useId, useRef } from 'react'
import { selectionActions, type SelectionAction, type SelectionActionAvailability } from './selection-actions.js'
import styles from './selection-bar.module.css'

/**
 * The selection bar (melody toolkit M6 step 6.3; spec §5.4, §11; Joni's layout, 2026-10-31): the deterministic actions
 * on the selected notes, in the spec's order, then a slot for Keep going (M7.2) and More actions. Each action's result
 * is shown as a ghost option by the editor, never written before Keep; Longer and Shorter are plain edits.
 *
 * - Exact intervals changes what Sequence and Mirror do: a checkbox in More actions, or Shift held on one click. While it
 *   is on, a chip at the end says so and turns it off.
 * - An unavailable action is `aria-disabled` with no reason line, except the reason given (no key), shown once and
 *   describing each action it applies to.
 */

export interface SelectionBarProps {
  /** How many notes are selected */
  count: number
  availability: Record<SelectionAction, SelectionActionAvailability>
  isExact: boolean
  onExactChange: (isExact: boolean) => void
  onAction: (action: SelectionAction, options: { isExact: boolean }) => void
  /** Longer has room before the next note */
  canLengthen: boolean
  /** Shorter keeps at least one grid step */
  canShorten: boolean
  onLonger: () => void
  onShorter: () => void
  /** Keep going (M7.2): at the right end, set off from the deterministic actions */
  keepGoing?: ReactNode
  className?: string
}

export const SelectionBar: FC<SelectionBarProps> = props => {
  const {
    count,
    availability,
    isExact,
    onExactChange,
    onAction,
    canLengthen,
    canShorten,
    onLonger,
    onShorter,
    keepGoing,
    className,
  } = props
  const reasonId = useId()
  const barRef = useRef<HTMLDivElement>(null)

  /** The ✕ leaves with its chip: focus goes to the first action, not to the page (Joni) */
  const handleExactOff = () => {
    onExactChange(false)
    barRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
  }
  /** The one reason shown, if any action has one */
  const reason = selectionActions.map(({ action }) => availability[action].reason).find(Boolean)

  const handleAction = (action: SelectionAction) => (event: MouseEvent<HTMLButtonElement>) => {
    if (!availability[action].isAvailable) {
      return
    }
    onAction(action, { isExact: isExact || event.shiftKey })
  }

  return (
    <div
      ref={barRef}
      role="toolbar"
      aria-label={`Actions for ${count} selected notes`}
      className={className ? `${styles.bar} ${className}` : styles.bar}
      data-testid="selection-bar"
    >
      <span className={styles.actions}>
        {selectionActions.map(({ action, label }) => {
          const { isAvailable, reason: own } = availability[action]
          return (
            <Button
              key={action}
              size="small"
              onClick={handleAction(action)}
              aria-disabled={isAvailable ? undefined : 'true'}
              aria-describedby={own ? reasonId : undefined}
            >
              {label}
            </Button>
          )
        })}
      </span>
      {reason ? (
        <span id={reasonId} className={styles.reason}>
          {reason}
        </span>
      ) : null}
      {isExact ? (
        <span className={styles.chip}>
          <span>Exact intervals</span>
          <IconButton size="small" aria-label="Turn off exact intervals" onClick={handleExactOff}>
            <Close fontSize="small" />
          </IconButton>
        </span>
      ) : null}
      {keepGoing ? <span className={styles.keepGoing}>{keepGoing}</span> : null}
      <Menu>
        <MenuTrigger
          render={
            <IconButton size="small" aria-label="More actions">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          }
        />
        <MenuContent>
          <MenuCheckboxItem checked={isExact} onCheckedChange={checked => onExactChange(checked)}>
            Exact intervals
          </MenuCheckboxItem>
          <MenuSeparator />
          <MenuItem disabled={!canLengthen} aria-disabled={canLengthen ? undefined : 'true'} onClick={onLonger}>
            Longer
          </MenuItem>
          <MenuItem disabled={!canShorten} aria-disabled={canShorten ? undefined : 'true'} onClick={onShorter}>
            Shorter
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  )
}
